import { SelectionIframePostMessageApi } from 'cadenza/workbook/selection-cart/selection-iframe-post-message-api';
import { DrillthroughIframePostMessageApi } from 'cadenza/workbook/selection-cart/drillthrough-iframe-post-message-api';
import { ClientPostMessageApi } from 'cadenza/utils/post-message/client-post-message-api';
import { MessageTypes } from 'cadenza/utils/post-message/post-message-api';
import { updateColorConfiguration } from 'cadenza/utils/colors/colors';
import { registerCloseAllPopupsIframeApiListener } from 'cadenza/workbook/view/view-content/close-all-popup-synchronization';
import { NotificationIframePostMessageApi } from 'cadenza/workbook/view/view-content/notification-iframe-post-message-api';
import { setupAjaxForPublishedVersion } from 'cadenza/workbook-version-setup';
import { WorkbookPostMessageTypes } from 'cadenza/workbook/view/view-content/workbook-post-message-types';

/**
 * Empty selection, used for removing the selection in the backend
 *
 * @type {SelectionRecordsDto}
 */
export const EMPTY_SELECTION = { };

export class WorkbookIframePostMessageApi extends ClientPostMessageApi {

  /**
   * API component for selection related activity.
   *
   * @type {SelectionIframePostMessageApi}
   */
  _selectionIframePostMessageApi;

  /**
   * API component for drill-down, drill-through and individual value table actions.
   *
   * @type {DrillthroughIframePostMessageApi}
   */
  _drillthroughIframePostMessageApi;

  /**
   * API component for handling view notifications and change.
   *
   * @type {NotificationIframePostMessageApi}
   */
  _notificationIframePostMessageApi;

  constructor (options) {
    super(options);
    this.addEventListener(MessageTypes.INIT, (event) => {
      const {
        colorConfiguration: newColorConfiguration,
        versionMode,
        repositoryName
      } = event.detail.parentInitParams;
      updateColorConfiguration(newColorConfiguration);
      setupAjaxForPublishedVersion(versionMode, repositoryName);
    });

    this._selectionIframePostMessageApi = new SelectionIframePostMessageApi(this, options);
    this._drillthroughIframePostMessageApi = new DrillthroughIframePostMessageApi(this, options);
    this._notificationIframePostMessageApi = new NotificationIframePostMessageApi(this, options);

    // Register listeners on close-all popup actions on the top window's and this iframe's document
    // to synchronize the closing of all remaining popups among all other windows.
    registerCloseAllPopupsIframeApiListener(this);
  }

  openFeatureInfoSettingsDialog () {
    return this.request({
      type: WorkbookPostMessageTypes.OPEN_FEATURE_INFO_SETTINGS_DIALOG
    });
  }

  /**
   * API module for handling matters concerning selection (on the iFrame side).
   *
   * @return {SelectionIframePostMessageApi} Selection API
   */
  get selection () {
    return this._selectionIframePostMessageApi;
  }

  /**
   * API module for handling drillthrough, drill-down and individual tables
   * dialogue (on the iFrame side).
   *
   * @return {DrillthroughIframePostMessageApi} Drillthrough API
   */
  get drillthrough () {
    return this._drillthroughIframePostMessageApi;
  }

  /**
   * API module for handling view notifications and change (on the iFrame side).
   *
   * @return {NotificationIframePostMessageApi} View notification API
   */
  get notification () {
    return this._notificationIframePostMessageApi;
  }

  showToastMessage (text, options) {
    return this.request({ type: WorkbookPostMessageTypes.TOAST_MESSAGE, detail: { text, options } });
  }

  /*
   * Inform the parent frame about loading state on the iframe.
   *
   * @param {LOADING_STATES} loadingState
   * @return {Promise<boolean|NoEndpointDefinedError>}
   *   A promise reflecting the receiving of the request on parent frame. First argument states the
   *   cancellation of the request by the recipient via event#preventDefault().
   */
  setLoadingState (loadingState) {
    return this.request({
      type: WorkbookPostMessageTypes.SET_LOADING_STATE,
      detail: {
        loadingState
      }
    });
  }

}
